import React, { useState, useEffect, useCallback } from 'react';
import { Users, Check, X, Trash2, Shield, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load users');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateUser = async (email, isApproved, role = 'admin') => {
    setActionLoading(email);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_approved: isApproved, role }),
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to update user');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    
    setActionLoading(email);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        fetchUsers();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to delete user');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleIcon = (role, isApproved) => {
    if (role === 'super_admin') return <ShieldCheck className="w-5 h-5 text-gold" />;
    if (isApproved) return <Shield className="w-5 h-5 text-green-500" />;
    return <ShieldAlert className="w-5 h-5 text-red-400" />;
  };

  const getRoleBadge = (role, isApproved, email) => {
    if (email === 'bm@villa.ooo') {
      return (
        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
          Super Admin
        </span>
      );
    }
    if (isApproved) {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
          Approved
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full font-medium">
        Pending
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gold" />
          <h2 className="text-lg font-medium text-night">User Management</h2>
          <span className="text-gray-400 text-sm">({users.length} users)</span>
        </div>
        <button
          onClick={fetchUsers}
          className="p-2 text-gray-400 hover:text-night transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {users.map((user) => {
          const isSuperAdmin = user.email === 'bm@villa.ooo';
          const isLoading = actionLoading === user.email;
          
          return (
            <div
              key={user.email}
              className={`p-4 flex items-center justify-between ${isLoading ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                {getRoleIcon(user.role, user.is_approved)}
                <div>
                  <div className="text-night font-medium">{user.email}</div>
                  <div className="text-gray-400 text-sm">
                    Registered: {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {getRoleBadge(user.role, user.is_approved, user.email)}
                
                {!isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    {user.is_approved ? (
                      <button
                        onClick={() => updateUser(user.email, false)}
                        disabled={isLoading}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                        title="Revoke Access"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => updateUser(user.email, true)}
                        disabled={isLoading}
                        className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
                        title="Approve Access"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => deleteUser(user.email)}
                      disabled={isLoading}
                      className="p-2 bg-gray-100 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors disabled:opacity-50"
                      title="Delete User"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {users.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            No users registered yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
